import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowRight, CircleCheck, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

type Status = 'idle' | 'sending' | 'sent' | 'error'

interface FormValues {
    name: string
    email: string
    company: string
    teamSize: string
    goal: string
    message: string
    website: string
}

const EMPTY: FormValues = {
    name: '',
    email: '',
    company: '',
    teamSize: '',
    goal: '',
    message: '',
    website: ''
}

const TEAM_SIZES = ['Just me', '2 to 10', '11 to 50', '51 to 200', 'More than 200']
const GOALS = ['Marketing', 'Lead generation', 'Email outreach', 'Bug fixing', 'All of it']
const EMAIL_PATTERN = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/

const fieldClass = 'mt-2 block w-full rounded-xl border border-white/10 bg-background/70 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 transition-colors focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/30'

const highlights = [
    "A live walkthrough of the Coordinator with your own product",
    "See all four teams working on a real request",
    "A plan for which agents to switch on first"
]

const DemoSection: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(EMPTY)
    const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')

    const update = (field: keyof FormValues, value: string): void => {
        setValues((current) => ({ ...current, [field]: value }))
        if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }))
    }

    const validate = (): boolean => {
        const next: Partial<Record<keyof FormValues, string>> = {}

        if (!values.name.trim()) next.name = 'Please tell us your name.'
        if (!EMAIL_PATTERN.test(values.email.trim())) next.email = 'Please enter a valid work email.'

        setErrors(next)
        return Object.keys(next).length === 0
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        if (status === 'sending' || !validate()) return

        setStatus('sending')
        setServerMessage('')

        try {
            const response = await fetch('/api/demo-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })
            const data = await response.json().catch(() => null) as { ok?: boolean, message?: string } | null

            if (!response.ok || !data?.ok) {
                setServerMessage(data?.message ?? 'Something went wrong. Please try again.')
                setStatus('error')
                return
            }

            setStatus('sent')
            setValues(EMPTY)
        }
        catch {
            setServerMessage('We could not reach the server. Check your connection and try again.')
            setStatus('error')
        }
    }

    return (
        <section id='demo' className='relative isolate overflow-hidden py-24 sm:py-32'>
            <div aria-hidden='true' className='bg-stars absolute inset-0 -z-10 opacity-60' />
            <div
                aria-hidden='true'
                className='absolute bottom-0 left-1/2 -z-10 h-[520px] w-[1100px] -translate-x-1/2 translate-y-1/3 rounded-full bg-[radial-gradient(ellipse,oklch(0.74_0.16_292/0.25),transparent_70%)]'
            />

            <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20'>
                <div className='lg:pt-6'>
                    <p className='font-mono text-xs uppercase tracking-[0.2em] text-primary'>
                        Book a demo
                    </p>
                    <h2 className='mt-4 font-heading text-3xl font-semibold tracking-tight text-balance sm:text-5xl'>
                        Put 21 agents to work on your product
                    </h2>
                    <p className='mt-5 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        Tell us a little about your team and we will set up a demo built around what you want to grow first.
                    </p>

                    <ul className='mt-8 space-y-4'>
                        {highlights.map((item) => (
                            <li key={item} className='flex gap-3'>
                                <CircleCheck className='mt-0.5 size-5 shrink-0 text-primary' aria-hidden='true' />
                                <span className='leading-relaxed text-foreground/90'>
                                    {item}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='rounded-3xl border border-white/10 bg-card/80 p-6 shadow-2xl shadow-black/30 backdrop-blur sm:p-8'>
                    {status === 'sent' ? (
                        <div className='flex min-h-[420px] flex-col items-center justify-center text-center' role='status'>
                            <span className='flex size-14 items-center justify-center rounded-full bg-primary/15 text-primary'>
                                <CircleCheck className='size-6' aria-hidden='true' />
                            </span>
                            <h3 className='mt-5 font-heading text-2xl font-semibold'>
                                Request received
                            </h3>
                            <p className='mt-2 max-w-sm leading-relaxed text-muted-foreground'>
                                Thanks for reaching out. We will reply to your email shortly to find a time for your demo.
                            </p>
                            <button
                                type='button'
                                onClick={() => setStatus('idle')}
                                className='mt-6 text-sm font-medium text-primary underline-offset-4 hover:underline'
                            >
                                Send another request
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} noValidate className='space-y-5'>
                            <div className='grid gap-5 sm:grid-cols-2'>
                                <div>
                                    <label htmlFor='demo-name' className='text-sm font-medium'>
                                        Full name
                                    </label>
                                    <input
                                        id='demo-name'
                                        name='name'
                                        autoComplete='name'
                                        value={values.name}
                                        onChange={(event) => update('name', event.target.value)}
                                        aria-invalid={Boolean(errors.name)}
                                        aria-describedby={errors.name ? 'demo-name-error' : undefined}
                                        className={cn(fieldClass, errors.name && 'border-destructive/70')}
                                        placeholder='Alex Morgan'
                                        maxLength={120}
                                    />
                                    {errors.name && (
                                        <p id='demo-name-error' className='mt-1.5 text-xs text-destructive'>
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor='demo-email' className='text-sm font-medium'>
                                        Work email
                                    </label>
                                    <input
                                        id='demo-email'
                                        name='email'
                                        type='email'
                                        autoComplete='email'
                                        value={values.email}
                                        onChange={(event) => update('email', event.target.value)}
                                        aria-invalid={Boolean(errors.email)}
                                        aria-describedby={errors.email ? 'demo-email-error' : undefined}
                                        className={cn(fieldClass, errors.email && 'border-destructive/70')}
                                        placeholder='alex@company.com'
                                        maxLength={200}
                                    />
                                    {errors.email && (
                                        <p id='demo-email-error' className='mt-1.5 text-xs text-destructive'>
                                            {errors.email}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor='demo-company' className='text-sm font-medium'>
                                    Company <span className='font-normal text-muted-foreground'>(optional)</span>
                                </label>
                                <input
                                    id='demo-company'
                                    name='company'
                                    autoComplete='organization'
                                    value={values.company}
                                    onChange={(event) => update('company', event.target.value)}
                                    className={fieldClass}
                                    placeholder='Company name'
                                    maxLength={160}
                                />
                            </div>

                            <div className='grid gap-5 sm:grid-cols-2'>
                                <div>
                                    <label htmlFor='demo-team-size' className='text-sm font-medium'>
                                        Team size
                                    </label>
                                    <select
                                        id='demo-team-size'
                                        name='teamSize'
                                        value={values.teamSize}
                                        onChange={(event) => update('teamSize', event.target.value)}
                                        className={cn(fieldClass, !values.teamSize && 'text-muted-foreground')}
                                    >
                                        <option value=''>Choose one</option>
                                        {TEAM_SIZES.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor='demo-goal' className='text-sm font-medium'>
                                        Start with
                                    </label>
                                    <select
                                        id='demo-goal'
                                        name='goal'
                                        value={values.goal}
                                        onChange={(event) => update('goal', event.target.value)}
                                        className={cn(fieldClass, !values.goal && 'text-muted-foreground')}
                                    >
                                        <option value=''>Choose one</option>
                                        {GOALS.map((goal) => (
                                            <option key={goal} value={goal}>
                                                {goal}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label htmlFor='demo-message' className='text-sm font-medium'>
                                    Anything else? <span className='font-normal text-muted-foreground'>(optional)</span>
                                </label>
                                <textarea
                                    id='demo-message'
                                    name='message'
                                    rows={4}
                                    value={values.message}
                                    onChange={(event) => update('message', event.target.value)}
                                    className={cn(fieldClass, 'resize-none')}
                                    placeholder='What would you like the agents to take off your plate?'
                                    maxLength={2000}
                                />
                            </div>

                            <div className='hidden' aria-hidden='true'>
                                <label htmlFor='demo-website'>Website</label>
                                <input
                                    id='demo-website'
                                    name='website'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={values.website}
                                    onChange={(event) => update('website', event.target.value)}
                                />
                            </div>

                            {status === 'error' && (
                                <p role='alert' className='rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive'>
                                    {serverMessage}
                                </p>
                            )}

                            <button
                                type='submit'
                                disabled={status === 'sending'}
                                className='inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-[0_0_40px_oklch(0.74_0.16_292/0.4)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70'
                            >
                                {status === 'sending' ? (
                                    <>
                                        <Loader2 className='size-4 animate-spin' aria-hidden='true' />
                                        Sending
                                    </>
                                ) : (
                                    <>
                                        Book my demo
                                        <ArrowRight className='size-4' aria-hidden='true' />
                                    </>
                                )}
                            </button>

                            <p className='text-center text-xs text-muted-foreground'>
                                We only use your details to set up your demo.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    )
}

export default DemoSection
